import React, { useMemo, useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { CalendarDays, Users, LogOut, Home, Shield, Tags, BarChart3, CreditCard, Mail, TrendingUp, Share2, ScanLine, Wrench, UserCog, Radar, HeartPulse, ScrollText, Activity, ListChecks, AlertOctagon, HeartHandshake, Sparkles, MessageSquare, Lock, ChevronDown, ChevronRight, Search, Settings, Wrench as WrenchIcon, LineChart, ClipboardList } from 'lucide-react';
import AdminFooter from '@/pages/admin/components/AdminFooter';
import MetaTokenExpiredBanner from '@/components/MetaTokenExpiredBanner';
import { AdminBadgeProvider, useAdminBadge } from '@/context/AdminBadgeContext';

const AdminLayoutInner = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const role = user?.role;
  const isAdmin = role === 'admin' || role === 'super_admin';
  const isSuperAdmin = role === 'super_admin';

  // Bundle 140 P0-4 - IA revamp. 4 top-level sections
  // (Operations, Analytics, Settings, Tools & Recovery). URLs are
  // preserved. Section headers are collapsible; expanded state is
  // persisted per admin via localStorage. A search-as-you-type
  // filter above the nav fuzzy-matches page labels.
  // Bundle 141 - each section also owns a landing route (`to`)
  // reachable by clicking the section header title.
  const sections = [
    {
      key: 'operations',
      label: 'Operations',
      to: '/admin/operations',
      icon: ClipboardList,
      items: [
        { to: '/admin/events', label: 'Events', icon: CalendarDays, requires: 'admin' },
        { to: '/admin/users', label: 'Users', icon: Users, testid: 'admin-users-directory-nav-link', requires: 'admin' },
        { to: '/admin/registrations', label: 'Registrations', icon: Users, testid: 'admin-registrations-nav-link', requires: 'admin' },
        { to: '/admin/payments', label: 'Payments', icon: CreditCard, requires: 'admin' },
        { to: '/admin/payments/by-event', label: 'Payments by event', icon: CreditCard, indent: true, testid: 'admin-payments-by-event-nav-link', requires: 'admin' },
        { to: '/admin/surveys', label: 'Surveys', icon: ListChecks, testid: 'admin-survey-ops-nav-link', requires: 'super_admin' },
        { to: '/admin/followups/pending', label: 'Follow-ups review', icon: Mail, indent: true, testid: 'admin-followups-pending-nav-link', requires: 'super_admin' },
        { to: '/admin/survey/email-templates', label: 'Email templates', icon: Mail, indent: true, testid: 'admin-email-templates-nav-link', requires: 'admin' },
        { to: '/admin/introductions', label: 'Introductions', icon: HeartHandshake, testid: 'admin-introductions-nav-link', requires: 'super_admin' },
        { to: '/admin/age-verification-pending', label: 'Age verification pending', icon: Shield, testid: 'admin-age-verification-pending-nav-link', requires: 'super_admin', badgePath: 'age_verification' },
        { to: '/check-in', label: 'Scan tickets', icon: ScanLine, testid: 'admin-checkin-scanner-link', requires: 'admin' },
      ],
    },
    {
      key: 'analytics',
      label: 'Analytics',
      to: '/admin/analytics',
      icon: LineChart,
      items: [
        { to: '/admin/analytics/overview', label: 'Overview', icon: BarChart3, requires: 'admin' },
        { to: '/admin/pixels', label: 'Pixels', icon: Radar, testid: 'admin-pixels-nav-link', requires: 'admin' },
        { to: '/admin/pricing-audit', label: 'Pricing audit', icon: BarChart3, testid: 'admin-pricing-audit-nav-link', requires: 'super_admin' },
        { to: '/admin/audit', label: 'Audit log', icon: ScrollText, testid: 'admin-audit-nav-link', requires: 'admin' },
        { to: '/admin/audit?high_risk=1', label: 'High risk', icon: ScrollText, indent: true, testid: 'admin-audit-high-risk-nav-link', requires: 'super_admin' },
      ],
    },
    {
      key: 'settings',
      label: 'Settings',
      to: '/admin/settings',
      icon: Settings,
      items: [
        { to: '/admin/user-roles', label: 'Roles', icon: UserCog, testid: 'admin-user-roles-nav-link', requires: 'super_admin' },
        { to: '/admin/settings/feature-flags', label: 'Feature flags', icon: Sparkles, testid: 'admin-feature-flags-nav-link', requires: 'super_admin' },
        { to: '/admin/settings/integrations', label: 'Integration secrets', icon: Lock, testid: 'admin-integration-secrets-nav-link', requires: 'super_admin' },
        { to: '/admin/settings/email-webhooks', label: 'Email webhook receipts', icon: Mail, testid: 'admin-email-webhooks-nav-link', requires: 'super_admin' },
        { to: '/admin/taxonomies', label: 'Taxonomies', icon: Tags, requires: 'admin' },
        { to: '/admin/progression', label: 'Progression', icon: TrendingUp, requires: 'super_admin' },
        { to: '/admin/referrals', label: 'Referrals', icon: Share2, requires: 'super_admin' },
        { to: '/admin/payments/auto-verify-config', label: 'Payment auto-verify', icon: Mail, requires: 'super_admin' },
      ],
    },
    {
      key: 'tools',
      label: 'Tools & Recovery',
      to: '/admin/tools',
      icon: WrenchIcon,
      items: [
        { to: '/admin/health', label: 'Data health & framing sentinel', icon: Activity, testid: 'admin-data-health-nav-link', requires: 'admin' },
        { to: '/admin/integrations', label: 'Integrations health', icon: HeartPulse, testid: 'admin-integrations-nav-link', requires: 'admin' },
        { to: '/admin/incidents', label: 'Incident actions', icon: AlertOctagon, testid: 'admin-incidents-nav-link', requires: 'super_admin', badgePath: 'incidents_leak', badgeTone: 'red' },
        { to: '/admin/qa', label: 'QA cockpit', icon: AlertOctagon, testid: 'admin-qa-nav-link', requires: 'super_admin', pill: 'preview only' },
        { to: '/admin/advanced-tools', label: 'Data cleanup', icon: Wrench, testid: 'admin-maintenance-nav-link', requires: 'super_admin' },
        { to: '/admin/incident/legacy-survey-recipients', label: 'Incident recipients', icon: AlertOctagon, indent: true, testid: 'admin-incident-recipients-nav-link', requires: 'super_admin' },
        { to: '/admin/incident/pre-bundle89-recipients', label: 'Pre-Bundle-89 recipients', icon: AlertOctagon, indent: true, testid: 'admin-pre89-recipients-nav-link', requires: 'super_admin' },
        { to: '/admin/incident/drift-scan', label: 'Drift scan', icon: AlertOctagon, indent: true, testid: 'admin-drift-scan-nav-link', requires: 'super_admin' },
      ],
    },
  ];

  const permit = (item) => {
    if (item.requires === 'super_admin') return isSuperAdmin;
    if (item.requires === 'admin') return isAdmin;
    return true;
  };

  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(() => {
    try {
      const raw = localStorage.getItem('admin_nav_expanded_v1');
      if (raw) return JSON.parse(raw);
    } catch (_) { /* noop */ }
    return { operations: true, analytics: true, settings: true, tools: true };
  });
  useEffect(() => {
    try {
      localStorage.setItem('admin_nav_expanded_v1', JSON.stringify(expanded));
    } catch (_) { /* noop */ }
  }, [expanded]);

  const [badges, setBadges] = useState({});
  const location = useLocation();
  const { leakCount, refetchLeakCount } = useAdminBadge();

  // Bundle 165 - LEAK badge value comes from
  // `AdminBadgeContext`. Existing mount/focus/route-change
  // triggers stay; they now write to the context via
  // `refetchLeakCount()`.
  useEffect(() => {
    setBadges((s) => ({ ...s, incidents_leak: leakCount }));
  }, [leakCount]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    // Age verification pending count. Best-effort, non-blocking.
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/age-verification-pending`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
    }).then((r) => r.ok ? r.json() : null).then((d) => {
      if (!d) return;
      const pending = (d.users || []).filter(
        (u) => u.age_gate_status === 'pending_review').length;
      setBadges((s) => ({ ...s, age_verification: pending }));
    }).catch(() => { /* noop */ });
    // Bundle 164 A1 - LEAK count on the Incident actions link.
    refetchLeakCount();
  }, [isSuperAdmin, location.pathname, refetchLeakCount]);

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    const onFocus = () => refetchLeakCount();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [isSuperAdmin, refetchLeakCount]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sections.map((sec) => ({
      ...sec,
      items: sec.items.filter(permit).filter(
        (it) => !q || it.label.toLowerCase().includes(q)),
    })).filter((sec) => sec.items.length > 0);
    // Deps: query + role flags. `sections` is rebuilt each render.
  }, [query, isAdmin, isSuperAdmin]);

  const renderItem = ({ to, label, icon: Icon, testid, indent, badgePath, badgeTone, pill }) => {
    const badge = badgePath ? badges[badgePath] : 0;
    const toneClass = badgeTone === 'red'
      ? 'bg-red-600 text-white'
      : 'bg-accent-700 text-white';
    return (
      <NavLink
        key={to}
        to={to}
        data-testid={testid}
        className={({ isActive }) =>
          `flex items-center gap-2 ${indent ? 'pl-8 pr-3' : 'px-3'} py-1.5 rounded-lg text-sm font-medium transition-colors ${
            isActive
              ? 'bg-white/15 text-white'
              : 'text-primary-100 hover:bg-white/10 hover:text-white'
          }`
        }
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="flex-1 truncate">{label}</span>
        {badge > 0 ? (
          <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${toneClass}`} data-testid={`admin-nav-badge-${badgePath}`}>
            {badge}
          </span>
        ) : null}
        {pill ? (
          <span className="text-[9px] uppercase tracking-widest rounded bg-secondary-800 text-white px-1 py-0.5">{pill}</span>
        ) : null}
      </NavLink>
    );
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex">
      {/* Sidebar */}
      <aside className="w-60 dost-signature-bg-sidebar text-primary-50 flex flex-col">
        <div className="px-5 py-6 border-b border-white/10">
          <div className="flex items-center gap-2 mb-1">
            <img src="/logo-192.png" alt="Dostination" className="w-8 h-8 shadow-gold rounded-lg bg-white/95 p-0.5" />
            <span className="font-display text-lg font-bold">Dostination</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-secondary-300">
            <Shield className="h-3 w-3" /> Admin
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-2 overflow-y-auto" data-testid="admin-nav">
          {/* Bundle 140 P0-4 - search-as-you-type nav filter */}
          <div className="px-1 pb-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-primary-300" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search admin..."
                data-testid="admin-nav-search"
                className="w-full text-xs bg-white/10 border border-white/15 text-white placeholder:text-primary-300 rounded pl-7 pr-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-white/30"
              />
            </div>
          </div>

          {filtered.map((sec) => {
            const SecIcon = sec.icon;
            const open = !!expanded[sec.key] || !!query.trim();
            return (
              <div key={sec.key} data-testid={`admin-nav-section-${sec.key}`}>
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => setExpanded((s) => ({ ...s, [sec.key]: !s[sec.key] }))}
                    data-testid={`admin-nav-section-toggle-${sec.key}`}
                    className="p-1 text-secondary-300 hover:text-white transition-colors"
                    aria-label={`Toggle ${sec.label}`}
                  >
                    {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  </button>
                  <NavLink
                    to={sec.to}
                    data-testid={`admin-nav-section-link-${sec.key}`}
                    className={({ isActive }) =>
                      `flex-1 flex items-center gap-2 px-2 py-1 text-[10px] uppercase tracking-widest font-semibold transition-colors ${
                        isActive
                          ? 'text-white'
                          : 'text-secondary-300 hover:text-white'
                      }`
                    }
                  >
                    <SecIcon className="h-3 w-3" />
                    <span className="flex-1 text-left">{sec.label}</span>
                  </NavLink>
                </div>
                {open ? (
                  <div className="space-y-0.5 pt-1">
                    {sec.items.map(renderItem)}
                  </div>
                ) : null}
              </div>
            );
          })}
          {filtered.length === 0 ? (
            <div className="text-xs text-primary-300 px-3 py-2" data-testid="admin-nav-empty">
              No matches.
            </div>
          ) : null}
        </nav>

        <div className="p-3 border-t border-white/10 space-y-2">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 w-full text-xs text-primary-100 hover:text-white px-2 py-1.5 rounded-md hover:bg-white/10 transition-colors"
          >
            <Home className="h-3.5 w-3.5" /> Back to site
          </button>
          <div className="px-2 py-1.5 text-xs text-primary-300">
            <div className="truncate">{user?.email}</div>
            <div className="text-secondary-300">{user?.role}</div>
          </div>
          <Button
            onClick={async () => { await logout(); navigate('/'); }}
            variant="ghost"
            size="sm"
            className="w-full text-primary-100 hover:text-white hover:bg-white/10"
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto flex flex-col">
        <MetaTokenExpiredBanner />
        <div className="flex-1">
          <Outlet />
        </div>
        <AdminFooter />
      </main>
    </div>
  );
};

// Bundle 165 - wrap in the AdminBadgeProvider so pages can
// imperatively invalidate the LEAK count via useAdminBadge().
const AdminLayout = () => (
  <AdminBadgeProvider>
    <AdminLayoutInner />
  </AdminBadgeProvider>
);

export default AdminLayout;
